import React, { useEffect, useState } from 'react';
import { addPackage, deletePackage, getAllPackages } from '../../actions/admin';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  IconButton,
  TextField,
  Typography,
  FormControlLabel, 
  styled,
  Button,
  FormHelperText,
  Backdrop,
  CircularProgress,
  Checkbox,
  Card,
  CardContent,
  Container,
  Paper,
  Fade,
  Zoom,
  Snackbar,
  Alert,
} from '@mui/material';
import { Tour, DeleteForever, Copyright, CurrencyRupee, Add, LocalOffer } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import Coin from './Coin.jsx'

const StyledContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  paddingTop: '2rem',
  paddingBottom: '2rem',
  width: '100%',
}));

const GlassCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
  }
}));

const PackageCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: '12px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  height: 'fit-content',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
  }
}));

const FormCard = styled(GlassCard)(({ theme }) => ({
  padding: '2rem',
  maxWidth: '500px',
  margin: '0 auto',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.8)',
    '& fieldset': {
      borderColor: 'rgba(103, 126, 234, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(103, 126, 234, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#667eea',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#667eea',
    fontWeight: '500',
  },
}));

const NumberTextField = styled(StyledTextField)({
  '& input': {
    height: '40px',
    padding: '0px 14px !important',
    fontSize: '1rem',
  }
});

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '12px',
  padding: '12px 24px',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: '600',
  boxShadow: '0 4px 15px rgba(103, 126, 234, 0.4)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(103, 126, 234, 0.6)',
  }
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '12px',
  right: '12px',
  background: 'rgba(244, 67, 54, 0.1)',
  color: '#f44336',
  '&:hover': {
    background: 'rgba(244, 67, 54, 0.2)',
    transform: 'scale(1.1)',
  },
  transition: 'all 0.2s ease',
}));

const Label = styled(Typography)({
  marginBottom: '8px',
  fontWeight: '600',
  color: '#374151',
  fontSize: '0.95rem',
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: 'white',
  fontWeight: '700',
  marginBottom: '2rem',
  textAlign: 'center',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
}));

const PriceChip = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  color: 'white',
  padding: '4px 8px',
  borderRadius: '16px',
  fontSize: '0.8rem',
  fontWeight: '600',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
}));

const CoinDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 10px',
  background: 'rgba(251, 191, 36, 0.1)',
  borderRadius: '8px',
  border: '1px solid rgba(251, 191, 36, 0.2)',
}));

const Packages = () => {
  const { token } = useSelector(state => state.userSlice);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [coinValue, setCoinValue] = useState(0);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm({
    defaultValues: {
      name: '',
      coins: 0,
      price: '',
    }
  });

  const isPremium = watch('is_premium');

  const fetchPackages = async () => {
    const resp = await getAllPackages({ token });
    setPackages(resp?.packages ?? []);
  };

  const deleteOnePackage = async id => {
    setLoading(true);
    try {
      await deletePackage({ token, id });
      await fetchPackages();
      setToast({ open: true, message: 'Package deleted successfully!', severity: 'success' });
    } catch (error) {
      setToast({ open: true, message: 'Failed to delete package', severity: 'error' });
    }
    setLoading(false);
  };

  const addOnePackage = async data => {
    setLoading(true);
    try {
      await addPackage({ token, data });
      await fetchPackages();
      reset({
        name: '',
        price: '',
        coins: 0,
      });
      setToast({ open: true, message: 'Package added successfully!', severity: 'success' });
    } catch (error) {
      setToast({ open: true, message: 'Failed to add package', severity: 'error' });
    }
    setLoading(false);
  };

  // unlimted package
  // const handleCheckboxChange = (event) => {
  //   const checked = event.target.checked;
  //   setIsUnlimited(checked);
    
  //   if (checked) {
  //     setCoinValue(999999); 
  //     setValue('coins', 999999); 
  //   } 
  //   else{
  //     setValue('coins', 0); 
  //   }
  // };

  useEffect(() => {
    fetchPackages();
  }, []);

  return (
    <StyledContainer>
      <Container maxWidth="xl">
        <Backdrop
          open={loading}
          sx={{
            zIndex: 1010,
            backdropFilter: 'blur(4px)',
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: '#667eea' }} />
        </Backdrop>

        <Grid container spacing={4}>
          {/* All Packages Section */}
          <Grid item xs={12} lg={6}>
            <SectionTitle variant='h4'>
              <LocalOffer sx={{ mr: 1, verticalAlign: 'middle' }} />
              All Packages
            </SectionTitle>
            
            <Grid container spacing={2}>
              {packages.map((pack, index) => (
                <Grid item xs={12} sm={6} key={pack._id}>
                  <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                    <PackageCard>
                      <DeleteButton
                        onClick={() => deleteOnePackage(pack._id)}
                        size="small"
                      >
                        <DeleteForever />
                      </DeleteButton>
                      
                      <CardContent sx={{ p: 2, pr: 5 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            textTransform: 'capitalize',
                            fontWeight: '600',
                            mb: 1.5,
                            color: '#1f2937',
                            fontSize: '1.1rem',
                          }}
                        >
                          {pack.name}
                        </Typography>
                        
                        <Grid container spacing={1.5} alignItems="center" justifyContent="space-between">
                          <Grid item>
                            <CoinDisplay>
                              <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                                Coins:
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: '700',
                                  color: '#f59e0b',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  fontSize: '0.95rem',
                                }}
                              >
                                {pack?.coins}
                                <Coin size="1rem" />
                              </Typography>
                            </CoinDisplay>
                          </Grid>
                          
                          <Grid item>
                            <PriceChip>
                              <CurrencyRupee sx={{ fontSize: '0.8rem' }} />
                              {pack.price}
                            </PriceChip>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </PackageCard>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Add New Package Section */}
          <Grid item xs={12} lg={6}>
            <SectionTitle variant='h4'>
              <Add sx={{ mr: 1, verticalAlign: 'middle' }} />
              Add New Package
            </SectionTitle>
            
            <Fade in={true} timeout={800}>
              <FormCard>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Label>Package Name</Label>
                      <StyledTextField
                        fullWidth
                        placeholder='Enter the package name'
                        {...register('name', { required: 'Name is required!' })}
                        error={!!errors?.name}
                      />
                      {errors?.name && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {errors?.name?.message}
                        </FormHelperText>
                      )}
                    </Grid>
               
                    <Grid item xs={12}>
                      <Label>Coins</Label>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <NumberTextField
                          type='number'
                          inputProps={{
                            min: 0,
                            disabled: isUnlimited
                          }}
                          sx={{ width: '140px' }}
                          placeholder="0"
                          {...register('coins', { required: true })}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Coin size="1.5rem" />
                          <Typography variant="body2" color="text.secondary">
                            Coins
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                
                    <Grid item xs={12}>
                      <Label>Price (in Rupees)</Label>
                      <StyledTextField
                        fullWidth
                        placeholder='Enter the price of the package'
                        type="number"
                        InputProps={{
                          startAdornment: <CurrencyRupee sx={{ color: '#667eea', mr: 1 }} />,
                        }}
                        {...register('price', {
                          required: 'Price is required!',
                          min: {
                            value: 1,
                            message: 'Price must be greater than 0'
                          }
                        })}
                        error={!!errors?.price}
                      />
                      {errors?.price && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {errors?.price?.message}
                        </FormHelperText>
                      )}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <GradientButton
                        fullWidth
                        variant='contained'
                        size="large"
                        onClick={handleSubmit(addOnePackage)}
                        startIcon={<Add />}
                      >
                        Add Package
                      </GradientButton>
                    </Grid>
                  </Grid>
                </CardContent>
              </FormCard>
            </Fade>
          </Grid>
        </Grid>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            variant="filled"
            sx={{
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </StyledContainer>
  );
};

export default Packages;